import {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {DocumentsPerProcess} from './DocumentsPerProcess';
import {IDocumentsPerProcessProps} from './IDocumentsPerProcessProps';
import {useUnit} from '../../../hooks/services/useUnit';
import {ViewUnit} from '../../../models/ViewUnit';
import {ITag} from '@fluentui/react';
import {TagInput} from './TagInput';
import {HeadingWithDecoration} from '../../common/HeadingWithDecoration';
import {useMasterProcess} from '../../../hooks/services/useMasterProcess';
import {SlimMasterProcessDto} from '../../../services/generated';
import {TheButton} from '../../common/TheButton';
import styles from './DocumentsPerProcess.module.scss';

export const DocumentsPerProcessAdmin: FC<IDocumentsPerProcessProps> = () => {
  const [allUnits, setAllUnits] = useState<ViewUnit[]>([]);
  const [allProcesses, setAllProcesses] = useState<SlimMasterProcessDto[]>([]);
  const {getPublishedUnits} = useUnit();
  const {getMasterProcessOrderedFlat} = useMasterProcess();

  const [selectedUnitsTags, setSelectedUnitsTags] = useState<ITag[]>([]);
  const [selectedProcessesTags, setSelectedProcessesTags] = useState<ITag[]>([]);
  const [selectedBranchesTags, setSelectedBranchesTags] = useState<ITag[]>([]);

  const [selectedUnitsIds, setSelectedUnitsIds] = useState<number[] | undefined>(undefined);
  const [selectedProcessesIds, setSelectedProcessesIds] = useState<number[] | undefined>(undefined);
  const [startSearch, setStartSearch] = useState(false);

  const getITagsFromPublishedUnits = useCallback((data: ViewUnit[]): ITag[] => {
    return data.map(item => ({key: item.id, name: item.title || ''}));
  }, []);

  const getITagsFromProcesses = useCallback((data: SlimMasterProcessDto[]): ITag[] => {
    return data.map(item => ({key: item.id, name: item.title || ''}));
  }, []);

  const clearSelectedUnits = () => {
    setSelectedUnitsTags([]);
    setSelectedUnitsIds(undefined);
  };

  const getAllChildren = useCallback(
    (parentId: number): SlimMasterProcessDto[] => {
      const children = allProcesses.filter(item => item.parentId === parentId);

      return children.flatMap(child => [child, ...getAllChildren(child.id)]);
    },
    [allProcesses]
  );

  const getBranch = useCallback(
    (processIds: number[]): ITag[] => {
      const processes = allProcesses.filter(item => processIds.includes(item.id));
      if (processes.length === 0) return [];

      const result: SlimMasterProcessDto[] = [];
      processes.forEach(process => {
        result.push(process);
        const children = getAllChildren(process.id);
        result.push(...children);
      });
      return result.map(item => ({key: item.id, name: item.title || ''}));
    },
    [allProcesses, getAllChildren]
  );

  useEffect(() => {
    if (!selectedBranchesTags || selectedBranchesTags.length === 0) {
      setSelectedProcessesTags([]);
      setSelectedProcessesIds(undefined);
    }

    if (selectedBranchesTags?.length > 0) {
      const selectedProcesses = selectedBranchesTags?.map(item => +item.key);

      var result = getBranch(selectedProcesses);
      if (result) {
        setSelectedProcessesTags(result);
      }
    }
  }, [selectedBranchesTags, getBranch]);

  const clearSelectedProcesses = () => {
    setSelectedProcessesTags([]);
    setSelectedProcessesIds(undefined);
    setSelectedBranchesTags([]);
  };

  const searchResult = useCallback(() => {
    setStartSearch(true);
    const selectedUnits = selectedUnitsTags?.map(item => +item.key);
    const selectedProcesses = selectedProcessesTags?.map(item => +item.key);
    setSelectedUnitsIds(selectedUnits && selectedUnits.length > 0 ? selectedUnits : undefined);
    setSelectedProcessesIds(
      selectedProcesses && selectedProcesses.length > 0 ? selectedProcesses : undefined
    );
  }, [selectedUnitsTags, selectedProcessesTags]);

  const disableClearProcessesButton = useMemo((): boolean => {
    return !selectedProcessesTags || selectedProcessesTags.length === 0;
  }, [selectedProcessesTags]);

  const disableClearUnitsButton = useMemo((): boolean => {
    return !selectedUnitsTags || selectedUnitsTags.length === 0;
  }, [selectedUnitsTags]);

  useEffect(() => {
    (async () => {
      const response = await getPublishedUnits();
      if (response && response.result) {
        setAllUnits(response.result);
      }
    })();
  }, [getPublishedUnits]);

  useEffect(() => {
    (async () => {
      const response = await getMasterProcessOrderedFlat();
      if (response && response.result) {
        const filteredProcesses = response.result.filter(x => x.deleted === false);
        setAllProcesses(filteredProcesses);
      }
    })();
  }, [getMasterProcessOrderedFlat]);

  return (
    <>
      <HeadingWithDecoration text="Documents per Process" />
      <div>
        <TagInput
          label="Select Unit(s) or leave empty to select all"
          elements={getITagsFromPublishedUnits(allUnits)}
          setValue={setSelectedUnitsTags}
          inputLimit={200}
          selectedItems={selectedUnitsTags}
          setSelectedItems={setSelectedUnitsTags}
        />
         <TagInput
          label="Select branch(es). Changing this value will overwrite the selected processes below"
          elements={getITagsFromProcesses(allProcesses)}
          setValue={setSelectedBranchesTags}
          inputLimit={200}
          selectedItems={selectedBranchesTags}
          setSelectedItems={setSelectedBranchesTags}
        />
        <TagInput
          label="Select single Process(es) or leave empty to select all"
          elements={getITagsFromProcesses(allProcesses)}
          setValue={setSelectedProcessesTags}
          inputLimit={200}
          selectedItems={selectedProcessesTags}
          setSelectedItems={setSelectedProcessesTags}
        />
       
        <div className={styles.footer_container}>
          <TheButton
            disabled={disableClearUnitsButton}
            primary
            iconProps={{iconName: 'clearFilter'}}
            onClick={() => clearSelectedUnits()}>
            Clear Units
          </TheButton>
          <TheButton
            disabled={disableClearProcessesButton}
            primary
            iconProps={{iconName: 'clearFilter'}}
            onClick={() => clearSelectedProcesses()}>
            Clear Processes
          </TheButton>

          <TheButton
            primary
            iconProps={{iconName: 'navigateForward'}}
            onClick={() => searchResult()}>
            Search
          </TheButton>
        </div>
      </div>
      <DocumentsPerProcess
        startSearch={startSearch}
        setStartSearch={setStartSearch}
        unitIds={selectedUnitsIds}
        processIds={selectedProcessesIds}
      />
    </>
  );
};
